import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { DrawerLayout } from '@/layouts/DrawerLayout';
import { FormProvider, SubmitHandler, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Button, Box, Container, Theme, useMediaQuery, Typography, CardContent, CardHeader, Card, AlertTitle, Alert, Fab, ListItem, TextField, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import DrawerHeader from '@/components/Organisms/BasicDrawer/DrawerHeader.component';
import { LoadingButton } from '@mui/lab';
import List from '@mui/material/List';
import { Copy, Trash2Icon, Plus, NavigationIcon, FileCog, X, } from 'lucide-react';
import ConfirmModal from '@/components/Molecules/Modal/ConfirmModal.component';
import useBatchUpdateMutation from "@/services/batchUpdate/batchUpdate.mutations";
import { createBatchUpdatesAffectCountOptionsQuery, createBatchUpdatesByIdOptionsQuery } from "@/services/batchUpdate/batchUpdates.options";
import TextBox from "@/components/Molecules/Textbox/Textbox.component";
import { AutoComplete } from "@utilisourcepackagelibdev/utilisourcepackagelib";
import { createStatusesQueryOptions } from "@/services/ticket/ticket.options";
import useTicketTypes from "@/services/type/type.options";
import { createAllUsersQueryOptions } from "@/services/users/userProfile.options";
import CheckboxElement from "@/components/Molecules/Checkbox/CheckboxElement.component";
import { createCustomerOptionsQuery } from "@/services/customer/customer.options";
import { Customer } from "@/interfaces/schemas/customer.schema";
import CheckboxInput from "@/components/Atoms/Checkbox/CheckboxInput.component";
import { PostMassupdateMutationRequest } from "@/services/batchUpdate/batchUpdate.service";
import { DslibTicketQuery } from '@/services/batchUpdate/batchUpdate.service';

enum BatchUpdateWhatToUpdate {
  'Assignee' = 3, // { Column: 3, Value: //AgentID : number}
  'Type' = 2, // { Column: 2, Value: //Type : number }
  'Status' = 1, // { Column: 1, Value: //Status : number }
}

interface LinkedCustomer {
  CustomerId: number;
  CustomerName: string;
}

interface Status {
  StatusId: number;
  Name: string;
  Color: number;
  IsDefault: boolean;
  IsClosed: boolean;
  IsDontTrack: boolean;
  LinkedCustomers: LinkedCustomer[];
  TicketCount: number;
}

interface TicketType {
  Description: string;
  TypeId: number;
  Name: string;
}
interface User {
  AgentType: number;
  CustomerList: {
    CustomerID: number;
    CustomerName: string;
  }[];
  DisabledBy: number;
  DisabledDate: string;
  FilterFeatureOn: boolean;
  TimeZone: string;
  TimeZoneOffset: string;
  TypesList: {
    TypeID: number;
    TypeName: string;
  }[];
  agentid: number;
  archivetime: string;
  customer: number;
  eclassid: number;
  email: string;
  flags: number;
  name: string;
  phone: string;
  roleid: number;
  rolename: string;
}

const StatusCounter = memo(() => {
  const { control } = useFormContext();
  const count = useWatch({
    control,
    name: 'massupdate_query.FilterStatus',
    defaultValue: {}
  });

  return (
    <Typography variant="body2" color="text.secondary" sx={{ p: 2, pb: 0 }}>
      ({Object.values(count).filter(Boolean).length}) selected
    </Typography>
  );
});


const TypeCounter = memo(() => {
  const { control } = useFormContext();
  const count = useWatch({
    control,
    name: 'massupdate_query.FilterType',
    defaultValue: {}
  });

  return (
    <Typography variant="body2" color="text.secondary" sx={{ p: 2, pb: 0 }}>
      ({Object.values(count).filter(Boolean).length}) selected
    </Typography>
  );
});

const AssigneeCounter = memo(() => {
  const { control } = useFormContext();
  const count = useWatch({
    control,
    name: 'massupdate_query.FilterAssignee',
    defaultValue: {}
  });

  return (
    <Typography variant="body2" color="text.secondary" sx={{ p: 2, pb: 0 }}>
      ({Object.values(count).filter(Boolean).length}) selected
    </Typography>
  );
});

const CustomerCounter = memo(() => {
  const { control } = useFormContext();
  const count = useWatch({
    control,
    name: 'massupdate_query.FilterCustomer',
    defaultValue: {}
  });

  return (
    <Typography variant="body2" color="text.secondary" sx={{ p: 2, pb: 0 }}>
      ({Object.values(count).filter(Boolean).length}) selected
    </Typography>
  );
});

const BatchUpdatesDrawer = () => {


  const navigate = useNavigate();
  const params = useParams({ from: '/_auth/batch-updates/$batchUpdateId' });
  const { variant } = useSearch({ from: '/_auth/batch-updates/$batchUpdateId' })
  const [activeStep, setActiveStep] = useState(0);

  const methods = useForm({
    mode: 'all',
    // defaultValues: initialValues,
    // shouldUnregister: false,
    // resolver: zodResolver(permissionValuesSchema)
  });


  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const allUsers = useQuery(createAllUsersQueryOptions());
  const { ticketTypes } = useTicketTypes()

  const allCustomers = useSuspenseQuery(createCustomerOptionsQuery())
  const batchUpdate = useSuspenseQuery(createBatchUpdatesByIdOptionsQuery(params.batchUpdateId))
  const statuses = useSuspenseQuery(createStatusesQueryOptions())

  const { batchUpdateMutation, isUpdatingBatchUpdate } = useBatchUpdateMutation()

  const [isOpen, setIsOpen] = useState(false)
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openExecuteModal, setOpenExecuteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [filteredStatuses, setFilteredStatuses] = useState<Status[]>(statuses?.data || []);
  const [filteredTicketTypes, setFilteredTicketTypes] = useState<TicketType[]>(ticketTypes?.data || []);
  const [filteredAssignees, setFilteredAssignees] = useState<User[]>(allUsers?.data || []);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>(allCustomers?.data || []);

  const typeIndeterminate = useMemo(() => {
    return filteredTicketTypes.length > 0 && filteredTicketTypes.length < ticketTypes?.data?.length
  }, [filteredTicketTypes])

  const assigneeIndeterminate = useMemo(() => {
    return filteredAssignees.length > 0 && filteredAssignees.length < allUsers?.data?.length
  }, [filteredAssignees])

  const customerIndeterminate = useMemo(() => {
    return filteredCustomers.length > 0 && filteredCustomers.length < allCustomers?.data?.length
  }, [filteredCustomers])

  // const statusIndeterminate = useMemo(() => {
  //   return filteredStatuses.length > 0 && filteredStatuses.length < statuses?.data?.length
  // }, [filteredStatuses])

  const statusWatch = useWatch({
    control: methods.control,
    name: 'massupdate_query.FilterStatus',
    defaultValue: {}
  })

  const formValues = useWatch({
    control: methods.control,
    name: 'massupdate_query',
  });

  const debouncedQuery = useMemo(() => {
    return {
      ...batchUpdate.data.massupdate_query,
      filterAssignee: Object.entries(formValues?.FilterAssignee || {}).filter(([_, value]) => value === true).map(([key]) => parseInt(key)) || [0],
      filterStatus: Object.entries(formValues?.FilterStatus || {}).filter(([_, value]) => value === true).map(([key]) => parseInt(key)) || [0],
      filterType: Object.entries(formValues?.FilterType || {}).filter(([_, value]) => value === true).map(([key]) => parseInt(key)) || [0],
      filterCustomer: Object.entries(formValues?.FilterCustomer || {}).filter(([_, value]) => value === true).map(([key]) => parseInt(key)) || [0],
    } as DslibTicketQuery;
  }, [
    formValues?.FilterAssignee,
    formValues?.FilterStatus,
    formValues?.FilterType,
    formValues?.FilterCustomer
  ]);


  const affectCount = useQuery(createBatchUpdatesAffectCountOptionsQuery(debouncedQuery));


  const totalStatuses = statuses?.data?.length || 0;
  const selectedStatuses = Object.values(statusWatch).filter(Boolean).length;
  const statusIndeterminate = selectedStatuses > 0 && selectedStatuses < totalStatuses;
  const checkAllStatuses = selectedStatuses === totalStatuses && totalStatuses > 0;

  // Open drawer when component mounts
  useEffect(() => {
    setIsOpen(true)
  }, [])

  const handleClose = () => {
    setIsOpen(false)
    // Wait for animation to complete before navigating
    setTimeout(() => {
      navigate({ to: '/batch-updates' })
    }, 300) // Match this with MUI's transition duration
  }

  const handleCloseDrawer = () => {
    handleClose()
    setActiveStep(0);
    // methods.reset(editCustomerValues2);
    navigate({ to: '/batch-updates' });
  };

  const handleSubmit: SubmitHandler<any> = (formValues: any) => {
    console.log('formValues : ', formValues);

    if (variant === 'edit') {
      console.log('edit')
      const payload: PostMassupdateMutationRequest = {
        massupdate_id: batchUpdate.data.massupdate_id,
        massupdate_name: formValues.massupdate_name,
        'queryassignees[]': Object.entries(formValues.massupdate_query.FilterAssignee || {})
          .filter(([_, value]) => value === true)
          .map(([key]) => key.toString()) || ['0'],
        'querycustomers[]': Object.entries(formValues.massupdate_query.FilterCustomer || {})
          .filter(([_, value]) => value === true)
          .map(([key]) => key.toString()) || ['0'],
        'querystatus[]': Object.entries(formValues.massupdate_query.FilterStatus || {})
          .filter(([_, value]) => value === true)
          .map(([key]) => key.toString()) || ['0'],
        'querytypes[]': Object.entries(formValues.massupdate_query.FilterType || {})
          .filter(([_, value]) => value === true)
          .map(([key]) => key.toString()) || ['0'],
        // updatecustomer: formValues.massupdate_update[BatchUpdateWhatToUpdate] ? formValues.massupdate_update[1].Value.toString() : null,
        updateassignee: formValues.massupdate_update[BatchUpdateWhatToUpdate.Assignee] ? formValues.massupdate_update[BatchUpdateWhatToUpdate.Assignee].id : 0,
        updatestatus: formValues.massupdate_update[BatchUpdateWhatToUpdate.Status] ? formValues.massupdate_update[BatchUpdateWhatToUpdate.Status].id : 0,
        updatetype: formValues.massupdate_update[BatchUpdateWhatToUpdate.Type] ? formValues.massupdate_update[BatchUpdateWhatToUpdate.Type].id : 0,
      }
      console.log('payload : ', payload)
      batchUpdateMutation.mutate({
        actionType: 'updateBatchUpdate',
        payload: {
          id: batchUpdate.data.massupdate_id,
        },
        data: payload
      })
    } else {
      console.log('new')
      const payload: PostMassupdateMutationRequest = {
        // massupdate_id: batchUpdate.data.massupdate_id,
        massupdate_name: formValues.massupdate_name,
        'queryassignees[]': Object.entries(formValues.massupdate_query.FilterAssignee || {})
          .filter(([_, value]) => value === true)
          .map(([key]) => key.toString()) || ['0'],
        'querycustomers[]': Object.entries(formValues.massupdate_query.FilterCustomer || {})
          .filter(([_, value]) => value === true)
          .map(([key]) => key.toString()) || ['0'],
        'querystatus[]': Object.entries(formValues.massupdate_query.FilterStatus || {})
          .filter(([_, value]) => value === true)
          .map(([key]) => key.toString()) || ['0'],
        'querytypes[]': Object.entries(formValues.massupdate_query.FilterType || {})
          .filter(([_, value]) => value === true)
          .map(([key]) => key.toString()) || ['0'],
        // updatecustomer: formValues.massupdate_update[BatchUpdateWhatToUpdate] ? formValues.massupdate_update[1].Value.toString() : null,
        updateassignee: formValues.massupdate_update[BatchUpdateWhatToUpdate.Assignee] ? formValues.massupdate_update[BatchUpdateWhatToUpdate.Assignee].id : 0,
        updatestatus: formValues.massupdate_update[BatchUpdateWhatToUpdate.Status] ? formValues.massupdate_update[BatchUpdateWhatToUpdate.Status].id : 0,
        updatetype: formValues.massupdate_update[BatchUpdateWhatToUpdate.Type] ? formValues.massupdate_update[BatchUpdateWhatToUpdate.Type].id : 0,
      }
      console.log('payload : ', payload)
      batchUpdateMutation.mutate({
        actionType: 'createBatchUpdate',
        payload: payload,
        data: payload
      })
    }
    return undefined
  }

  const handleComplete = () => {
    console.log('handleComplete')
    // console.log(methods.getValues())
  }

  const handleDelete = () => {
    console.log('handleDelete')
    setIsDeleting(true);
    batchUpdateMutation.mutate({
      actionType: 'deleteBatchUpdate',
      payload: {
        id: batchUpdate.data.massupdate_id,
      }
    })
  };

  const handleExecute = () => {
    console.log('handleExecute')
    setOpenExecuteModal(true);
  }

  const handleConfirmExecute = () => {
    batchUpdateMutation.mutate({
      actionType: 'executeBatchUpdate',
      payload: {
        id: batchUpdate.data.massupdate_id
      }
    });
    setOpenExecuteModal(false);
  };

  interface UpdateItem {
    Column: number;
    Value: number;
  }

  const updateMappings = {
    [BatchUpdateWhatToUpdate.Status]: {
      data: statuses.data,
      idField: 'StatusId',
      nameField: 'Name'
    },
    [BatchUpdateWhatToUpdate.Type]: {
      data: ticketTypes.data,
      idField: 'TypeId',
      nameField: 'Name'
    },
    [BatchUpdateWhatToUpdate.Assignee]: {
      data: allUsers.data,
      idField: 'agentid',
      nameField: 'name'
    }
  };

  useEffect(() => {
    if (variant === 'edit') {

      console.log('edit')
      console.log(batchUpdate.data)
      console.log(ticketTypes.data)
      methods.setValue('massupdate_name', batchUpdate.data.massupdate_name)

      // Set individual filter values from the array
      batchUpdate.data.massupdate_query.FilterStatus?.forEach((status: number) => {
        methods.setValue(`massupdate_query.FilterStatus.${status}`, true);
      });

      batchUpdate.data.massupdate_query.FilterType?.forEach((type: number) => {
        methods.setValue(`massupdate_query.FilterType.${type}`, true);
      });

      batchUpdate.data.massupdate_query.FilterAssignee?.forEach((assignee: number) => {
        methods.setValue(`massupdate_query.FilterAssignee.${assignee}`, true);
      });

      batchUpdate.data.massupdate_query.FilterCustomer?.forEach((customer: number) => {
        methods.setValue(`massupdate_query.FilterCustomer.${customer}`, true);
      });

      // Map through the massupdate_update ACTIONS array to find matching Statuses/Types/Assignees to populate with RHF

      batchUpdate.data.massupdate_update?.forEach((updateItem: UpdateItem) => {
        const mapping = updateMappings[updateItem.Column as keyof typeof updateMappings];
        if (!mapping) return;

        const matchingItem = mapping.data?.find(
          (item: any) => item[mapping.idField] === updateItem.Value
        );

        if (matchingItem) {
          methods.setValue(`massupdate_update[${updateItem.Column}]`, {
            id: matchingItem[mapping.idField],
            label: matchingItem[mapping.nameField]
          });
        }
      });

    }
  }, [variant, batchUpdate?.data])

  // const statusFilterValues = useWatch({
  //   control: methods.control,
  //   name: 'massupdate_query.FilterStatus',
  //   defaultValue: {}
  // });

  // const checkedStatusCount = Object.values(statusFilterValues).filter(Boolean).length;
  // Calculate the number of checked statuses
  // const checkedStatusCount = useMemo(() =>
  //   Object.values(statusFilterValues || {}).filter(Boolean).length,
  //   [statusFilterValues]
  // );


  // useEffect(() => {
  //   console.log(statusFilter)
  // }, [statusFilter])



  return (
    <DrawerLayout
      open={isOpen}
      onClose={handleCloseDrawer}
      anchor="right"
      keepMounted
      width={'80%'}
      Header={
        <DrawerHeader
          close={handleCloseDrawer}
          title={variant === 'edit' ? 'Edit Batch Update' : 'New Batch Update'}
        />
      }
    >
      <FormProvider {...methods}>
        {/* @ts-ignore */}
        <Container maxWidth='xl' component='form' onSubmit={methods.handleSubmit(handleSubmit)} sx={{
          overflowY: 'auto',
          mt: 5,
          height: '98%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          mb: 4,
        }}>

          <Grid size={12} display="flex" flexDirection="column" flexWrap="wrap" >
            <Grid size={12} container rowGap={4} >
              <Alert severity="info" sx={{ width: '100%' }}>
                {useMemo(() => (
                  affectCount.isLoading ? 'Calculating...' : affectCount?.data?.Count + ' tickets will be affected by this update'
                ), [affectCount.isLoading, affectCount?.data?.Count])}
                {/* {affectCount.isLoading ? 'Calculating...' : affectCount?.data?.Count} tickets will be affected by this update */}
              </Alert>
              <Grid size={12} sx={{
                p: 2
              }}>
                <TextBox
                  label="Name"
                  name="massupdate_name"
                  fullWidth
                  required
                />

              </Grid>
              <Grid size={12} sx={{
                p: 2
              }}>
                <Grid size={12} container rowGap={4} >
                  <Grid size={{ xs: 12, md: 6, lg: 3, xl: 3 }} container >
                    <Card sx={{ height: 500, width: 360, }}>
                      <CardHeader title="Status Option" />
                      <CardContent>
                        <Box
                          sx={{ width: '100%', height: 500, maxWidth: 360, bgcolor: 'background.paper', overflowY: 'auto' }}
                        >
                          <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, p: 4, pl: 0, pt: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 1 }}>
                              <TextBox
                                name="statusFilter"
                                InputProps={{
                                  endAdornment: (
                                    <IconButton
                                      onClick={() => {
                                        setFilteredStatuses(statuses.data || []);
                                        methods.setValue('statusFilter', '');
                                      }}
                                      edge="end"
                                      size="small"
                                    >
                                      <X />
                                    </IconButton>
                                  ),
                                }}
                                size="small"
                                fullWidth
                                placeholder="Filter Statuses..."
                                onChange={(e) => {
                                  const value = methods.getValues('statusFilter');
                                  const searchTerm = value.toLowerCase();
                                  const filtered = statuses.data?.filter((status: Status) =>
                                    status.Name.toLowerCase().includes(searchTerm)
                                  );
                                  setFilteredStatuses(filtered || []);
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: 'background.default'
                                  },
                                  m: 0,
                                  p: 0,
                                }}
                              />
                            </Box>
                            <StatusCounter />
                          </Box>
                          <List dense>
                            {/* <ListItem key={'statusAll'} > */}
                            {/* <CheckboxInput
                                label="All"
                                checked={checkAllStatuses}
                                id="checkAllStatuses"
                                indeterminate={statusIndeterminate}
                                onDataChange={() => {
                                  if (checkAllStatuses) {
                                    console.log('checkAllStatuses and clean everything')
                                    // If all are checked, clear everything
                                    // const statuses = statuses.data?.map((status: Status) => status.StatusId) || [];
                                    statuses.data?.forEach((status: Status) => {
                                      methods.setValue(`massupdate_query.FilterStatus.${status.StatusId}`, false);
                                    });

                                    // methods.setValue('massupdate_query.FilterStatus', {});

                                  } else {
                                    console.log('checkAllStatuses and select all')
                                    // If none or some are checked, select all
                                    methods.setValue('massupdate_query.FilterStatus', statuses.data?.map((status: Status) => status.StatusId) || []);
                                  }



                                }}
                                sxProps={{
                                  fontWeight: 'bold',
                                  color: 'primary.main',
                                  m: 0,
                                  p: 0,
                                }}
                              /> */}
                            {/* </ListItem> */}
                            {filteredStatuses?.map((status: Status) => (
                              <ListItem key={status.StatusId} >
                                <CheckboxElement
                                  control={methods.control}
                                  id={`massupdate_query.FilterStatus.${status.StatusId}`}
                                  name={`massupdate_query.FilterStatus.${status.StatusId}`}
                                  label={status.Name}
                                  sx={{
                                    fontWeight: 'bold',
                                    color: 'primary.main',
                                    m: 0,
                                    p: 0,
                                  }}
                                />
                              </ListItem>

                            ))}

                          </List>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 3, xl: 3 }} container >
                    <Card sx={{ height: 500, width: 360, }}>
                      <CardHeader title="Type Option" />
                      <CardContent>
                        <Box

                          sx={{ width: '100%', height: 500, maxWidth: 360, bgcolor: 'background.paper', overflowY: 'auto' }}
                        >
                          <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, p: 4, pl: 0, pt: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 1 }}>
                              <TextBox
                                name="typeFilter"
                                InputProps={{
                                  endAdornment: (
                                    <IconButton
                                      onClick={() => {
                                        setFilteredStatuses(ticketTypes.data || []);
                                        methods.setValue('typeFilter', '');
                                      }}
                                      edge="end"
                                      size="small"
                                    >
                                      <X />
                                    </IconButton>
                                  ),
                                }}
                                size="small"
                                fullWidth
                                placeholder="Filter Ticket Types..."
                                onChange={(e) => {
                                  const value = methods.getValues('typeFilter');
                                  const searchTerm = value.toLowerCase();
                                  const filtered = ticketTypes.data?.filter((type: TicketType) =>
                                    type.Name.toLowerCase().includes(searchTerm)
                                  );
                                  setFilteredTicketTypes(filtered || []);
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: 'background.default'
                                  }
                                }}
                              />
                            </Box>
                            <TypeCounter />
                          </Box>
                          <List dense>
                            {filteredTicketTypes?.map((type: TicketType) => (
                              <ListItem key={type.TypeId} >
                                <CheckboxElement
                                  control={methods.control}
                                  id={`massupdate_query.FilterType.${type.TypeId}`}
                                  name={`massupdate_query.FilterType.${type.TypeId}`}
                                  indeterminate={typeIndeterminate}
                                  label={type.Name}

                                  sx={{
                                    fontWeight: 'bold',
                                    color: 'primary.main',
                                    m: 0,
                                    p: 0,
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 3, xl: 3 }} container >
                    <Card sx={{ height: 500, width: 360, }}>
                      <CardHeader title="Assignee Option" />
                      <CardContent>
                        <Box
                          sx={{ width: '100%', height: 500, maxWidth: 360, bgcolor: 'background.paper', overflowY: 'auto' }}
                        >
                          <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, p: 4, pl: 0, pt: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 1 }}>
                              <TextBox
                                name="assigneeFilter"
                                InputProps={{
                                  endAdornment: (
                                    <IconButton
                                      onClick={() => {
                                        setFilteredAssignees(allUsers.data || []);
                                        methods.setValue('assigneeFilter', '');
                                      }}
                                      edge="end"
                                      size="small"
                                    >
                                      <X />
                                    </IconButton>
                                  ),
                                }}
                                size="small"
                                fullWidth
                                placeholder="Filter Assignees..."
                                onChange={(e) => {
                                  const value = methods.getValues('assigneeFilter');
                                  const searchTerm = value.toLowerCase();
                                  const filtered = allUsers.data?.filter((user: User) =>
                                    user.name.toLowerCase().includes(searchTerm)
                                  );
                                  setFilteredAssignees(filtered || []);
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: 'background.default'
                                  }
                                }}
                              />
                            </Box>
                            <AssigneeCounter />
                          </Box>
                          <List dense>

                            {filteredAssignees?.map((assignee: User) => (
                              <ListItem key={assignee.agentid} >
                                <CheckboxElement
                                  id={`massupdate_query.FilterAssignee.${assignee.agentid}`}
                                  name={`massupdate_query.FilterAssignee.${assignee.agentid}`}
                                  label={assignee.name}
                                  control={methods.control}
                                  sx={{
                                    fontWeight: 'bold',
                                    color: 'primary.main',
                                    m: 0,
                                    p: 0,
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 3, xl: 3 }} container >

                    <Card sx={{ height: 500, width: 360, }}>
                      <CardHeader title="Customer Option" />
                      <CardContent>
                        <Box
                          sx={{ width: '100%', height: 500, maxWidth: 360, bgcolor: 'background.paper', overflowY: 'auto' }}
                        >
                          <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, p: 4, pl: 0, pt: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 1 }}>
                              <TextBox
                                name="customerFilter"
                                InputProps={{
                                  endAdornment: (
                                    <IconButton
                                      onClick={() => {
                                        setFilteredCustomers(allCustomers.data || []);
                                        methods.setValue('customerFilter', '');
                                      }}
                                      edge="end"
                                      size="small"
                                    >
                                      <X />
                                    </IconButton>
                                  ),
                                }}
                                size="small"
                                fullWidth
                                placeholder="Filter Customers..."
                                onChange={(e) => {
                                  const value = methods.getValues('customerFilter');
                                  const searchTerm = value.toLowerCase();
                                  const filtered = allCustomers.data?.filter((customer: Customer) =>
                                    customer.EntName.toLowerCase().includes(searchTerm)
                                  );
                                  setFilteredCustomers(filtered || []);
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: 'background.default'
                                  }
                                }}
                              />
                            </Box>
                            <CustomerCounter />
                          </Box>
                          <List dense>

                            {filteredCustomers?.map((customer: Customer) => (
                              <ListItem key={customer.CustomerEntId} >
                                <CheckboxElement
                                  control={methods.control}
                                  id={`massupdate_query.FilterCustomer.${customer.CustomerEntId}`}
                                  name={`massupdate_query.FilterCustomer.${customer.CustomerEntId}`}
                                  label={customer.EntName}
                                  sx={{
                                    fontWeight: 'bold',
                                    color: 'primary.main',
                                    m: 0,
                                    p: 0,
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={12} sx={{
                p: 2
              }}>
                <Card>
                  <CardHeader title="Update Actions" />
                  <CardContent>
                    <Grid size={12} container rowGap={4} >

                      <Grid size={{ xs: 12, md: 6, lg: 4, xl: 4 }} sx={{
                        p: 2
                      }}>
                        <AutoComplete
                          label="Update Status To..."
                          id={`massupdate_update.${BatchUpdateWhatToUpdate.Status.toString()}`}
                          options={statuses.data?.map((status: any) => ({
                            id: status.StatusId,
                            label: status.Name,
                          })) || []}
                          // required
                          loading={statuses?.isPending}
                          autocompleteProps={{
                            id: `massupdate_update.${BatchUpdateWhatToUpdate.Status.toString()}`,
                            loadingText: 'Loading Statuses...',
                          }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6, lg: 4, xl: 4 }} sx={{
                        p: 2
                      }}>
                        <AutoComplete
                          label="Update Type To..."
                          id={`massupdate_update.${BatchUpdateWhatToUpdate.Type.toString()}`}
                          options={ticketTypes.data?.map((ticketType: any) => ({
                            id: ticketType.TypeId,
                            label: ticketType.Name,
                          })) || []}
                          // required
                          loading={ticketTypes?.isPending}
                          autocompleteProps={{
                            id: `massupdate_update.${BatchUpdateWhatToUpdate.Type.toString()}`,
                            loadingText: 'Loading Ticket Types...',
                          }}
                        />
                      </Grid>


                      <Grid size={{ xs: 12, md: 6, lg: 4, xl: 4 }} sx={{
                        p: 2
                      }}>
                        <AutoComplete
                          label="Update Assignee To..."
                          id={`massupdate_update.${BatchUpdateWhatToUpdate.Assignee.toString()}`}
                          options={allUsers.data?.map((user: any) => ({
                            id: user.agentid,
                            label: user.name,
                          })) || []}
                          // required
                          loading={allUsers?.isPending}
                          autocompleteProps={{
                            id: `massupdate_update.${BatchUpdateWhatToUpdate.Assignee.toString()}`,
                            loadingText: 'Loading Assignees...',
                          }}
                        />

                      </Grid>
                    </Grid>

                  </CardContent>
                </Card>


              </Grid>
              <Fab variant="extended" color="primary" onClick={handleExecute} >
                <FileCog absoluteStrokeWidth style={{ marginRight: '8px' }} />
                Execute
              </Fab>
            </Grid>
          </Grid>

          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: (theme: Theme) => theme.palette.background.paper,
              borderTop: '1px solid',
              borderColor: (theme: Theme) => theme.palette.divider,
              p: 5,
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1rem',
              zIndex: 1051
            }}>
            <LoadingButton
              loading={isDeleting}
              color="error"
              startIcon={<Trash2Icon />}
              size='small'
              onClick={() => setOpenModal(true)}>
              Delete
            </LoadingButton>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={handleCloseDrawer} size='small'>
                Cancel & Close
              </Button>
              {openModal && (
                <ConfirmModal
                  open={openModal}
                  setOpen={setOpenModal}
                  title="Are you sure?"
                  content="This action will permanently delete this batch update."
                  onSuccess={handleDelete}
                  onClose={() => setOpenModal(false)}
                  cancelButtonText="Cancel"
                  confirmButtonText="Delete"
                />
              )}
              {openExecuteModal && (
                <ConfirmModal
                  open={openExecuteModal}
                  setOpen={setOpenExecuteModal}
                  title={`Execute Batch Update: ${batchUpdate.data.massupdate_name}`}
                  content={`Are you sure you want to execute this batch update? This will update all ${affectCount.data?.Count} matching tickets.`}
                  onSuccess={handleConfirmExecute}
                  onClose={() => setOpenExecuteModal(false)}
                  cancelButtonText="Cancel"
                  confirmButtonText="Execute"
                />
              )}
              <LoadingButton loading={isUpdatingBatchUpdate['updateBatchUpdate'] || isUpdatingBatchUpdate['createBatchUpdate']} variant="contained" type="submit" size='small'>
                Save
              </LoadingButton>
            </Box>
          </Box>
        </Container >

      </FormProvider >
    </DrawerLayout >
  )
}

export default BatchUpdatesDrawer